/**
 * Static blog posts for the marketing site. Replace or extend this array as you publish.
 * Images reference files in /public.
 */

export const BLOG_CATEGORY_FILTERS = [
  "All",
  "Buying Smarter",
  "Renovation Strategy",
  "Calgary Market Insights",
  "Case Studies",
];

export const blogPosts = [
  {
    id: "post-1",
    slug: "evaluate-renovation-property-calgary",
    title: "How to Evaluate a Renovation Property Before You Buy in Calgary",
    category: "Buying Smarter",
    excerpt:
      "Buying a home with renovation potential can be a smart move, but only if you understand what you are really buying. Here is how to look past finishes and evaluate the property properly before you commit.",
    date: "2026-03-18",
    readTime: "10 min read",
    featuredImage: "service-real-estate.jpg",
    featured: true,
    seoTitle:
      "How to Evaluate a Renovation Property Before You Buy in Calgary",
    seoDescription:
      "A practical Calgary guide to evaluating renovation properties before you buy, including layout, structure, permits, neighborhood fit, and budget risk.",
    content: `Buying a renovation property can create real upside. It can also create expensive problems if you fall in love with the wrong house for the wrong reasons.

A lot of buyers walk through a dated property and immediately start imagining what it could become. That is the easy part. The harder part is understanding whether the house, lot, layout, and neighborhood actually support the renovation you have in mind.

That is where better early decisions matter.

## Start with the layout, not the finishes

Cosmetic issues are usually the least important part of the decision.

Old flooring, dated cabinets, tired bathrooms, and worn paint can all be changed. What is much harder to fix is a house that has the wrong layout, poor natural flow, or structural limitations that make your renovation goals more expensive than expected.

When I look at a renovation property, I start by asking:

- Does the existing layout support modern living?
- Can the kitchen, entry, or main living space be improved without major structural complexity?
- Is there enough width, depth, and orientation on the lot to support the kind of changes the buyer wants?

A house with ugly finishes and good bones is often a much better opportunity than a nicer-looking house with a poor plan.

## Understand the real cost drivers

Many buyers underestimate where renovation budgets actually go.

The expensive parts are usually not the obvious visible finishes. The real cost drivers are often:

- structural changes
- mechanical upgrades
- permit requirements
- window and envelope work
- site conditions
- unknowns hidden behind walls

That does not mean you should avoid older homes. It means you need to evaluate them properly before assuming the renovation will be straightforward.

## Think about the neighborhood fit

In Calgary, renovation value is strongly tied to location and neighborhood expectations.

Ask yourself:

- Does the planned renovation match the value ceiling of the area?
- Will the finished product make sense for the street and surrounding homes?
- Are you improving the property in a way that supports long-term resale?

A renovation should not just make the home better for today. It should make sense for the neighborhood and the next buyer too.

## Consider permit and planning risk early

Some projects are simple cosmetic upgrades. Others quickly move into permit, design, and coordination territory.

If your goals include opening walls, building additions, changing exterior massing, or significantly reworking the home, you need to think beyond inspiration photos. You need to understand what the path actually looks like.

That includes:

- planning feasibility
- permit complexity
- design constraints
- construction sequencing
- budget alignment

## Buy with a broader perspective

The best renovation properties are not always the most obvious ones. They are the ones where the layout, structure, lot, and value opportunity all line up.

That is why I approach these decisions differently. Real estate, design, and construction are connected. Looking at only one part of the process can lead to expensive mistakes later.

If you are considering a Calgary property with renovation potential, I can help you evaluate it before you commit.

Thinking about buying a renovation property in Calgary? Send me the listing and a few notes on what you want to do with it. I'll give you my take on whether the opportunity makes sense.`,
  },
  {
    id: "post-2",
    slug: "biggest-renovation-mistakes-homeowners-make",
    title: "The Biggest Mistakes Homeowners Make Before Starting a Renovation",
    category: "Renovation Strategy",
    excerpt:
      "Most renovation problems do not start during construction. They start before construction begins. Here are the mistakes that cost homeowners the most time, money, and flexibility.",
    date: "2026-04-02",
    readTime: "9 min read",
    featuredImage: "service-design.png",
    featured: false,
    seoTitle: "The Biggest Mistakes Homeowners Make Before Starting a Renovation",
    seoDescription:
      "Avoid the most common renovation mistakes homeowners make before starting, including poor planning, unrealistic budgets, weak scope definition, and bad sequencing.",
    content: `Most renovation problems are not caused by one dramatic mistake. They come from a series of small early decisions that were never properly thought through.

By the time construction starts, the budget is already under pressure, the scope is unclear, and the homeowner is reacting instead of leading the process.

That is why the planning stage matters so much.

## Mistake 1: Starting with finishes instead of priorities

A lot of people begin with fixtures, materials, colors, and inspiration images.

That is understandable, but it is not where the project should start.

The first question is not "What should this look like?" It is "What does this project need to solve?"

That might mean:

- improving layout
- increasing function
- correcting poor flow
- creating better storage
- preparing the property for resale
- aligning the renovation with long-term goals

If those priorities are not clear early, the project drifts.

## Mistake 2: Underestimating the real budget

Most homeowners do not underestimate the cost of tile or cabinets. They underestimate everything around them.

The biggest misses are often:

- design and drawing work
- permit requirements
- demolition surprises
- structural changes
- electrical and mechanical updates
- site protection and sequencing

A renovation budget should not just cover the visible parts. It should reflect how the work actually gets built.

## Mistake 3: Not defining scope clearly enough

Scope problems create almost every other problem.

If the renovation starts with vague language like "update the kitchen" or "open things up," the project can quickly expand without proper decision-making.

Clear scope helps everyone:

- homeowner
- designer
- contractor
- permit reviewers
- trades

It reduces confusion, change orders, and decision fatigue.

## Mistake 4: Ignoring construction sequencing

Even good ideas can become expensive if the work is sequenced poorly.

For example:

- opening walls after finishes are already ordered
- choosing layout changes too late
- discovering permit requirements after design decisions were made
- beginning demolition before key decisions are finalized

Good projects are not just well designed. They are well sequenced.

## Mistake 5: Treating real estate, design, and construction as separate conversations

This is one of the biggest hidden problems.

Homeowners often make property and renovation decisions in separate silos:

- purchase first
- design later
- construction reality last

That separation causes missed opportunities and expensive friction.

The strongest renovations happen when the decision-making is connected from the start.

## Start smarter, not faster

A successful renovation does not require endless planning. It requires the right planning.

Better early thinking creates:

- clearer scope
- stronger budgeting
- fewer surprises
- better outcomes

That is where projects get stronger before construction even begins.

If you are planning a renovation and want a second opinion before you go too far down the wrong path, reach out. A small planning conversation early can prevent a much bigger problem later.`,
  },
  {
    id: "post-3",
    slug: "lakeview-mid-century-modern-renovation-case-study",
    title: "Case Study: Lakeview Mid Century Modern Renovation",
    category: "Case Studies",
    excerpt:
      "This Lakeview project is a good example of how thoughtful planning, design discipline, and construction alignment can transform a dated bungalow without losing what made the property worth improving in the first place.",
    date: "2026-04-10",
    readTime: "8 min read",
    featuredImage: "service-construction.jpg",
    featured: false,
    seoTitle: "Lakeview Mid Century Modern Renovation Case Study",
    seoDescription:
      "A Calgary renovation case study showing how a dated bungalow was transformed through integrated design, planning, and construction thinking.",
    content: `Not every successful renovation comes from making a home bigger. Sometimes the real opportunity is making it clearer, stronger, and more intentional.

That was the goal with this Lakeview mid century modern renovation.

The property already had qualities worth preserving: a strong form, a good lot, and a neighborhood context that supported thoughtful improvement. The challenge was modernizing the house in a way that respected those strengths while improving daily function and overall presentation.

## The challenge

Like many older homes, the opportunity was obvious, but so were the constraints.

The project needed to balance:

- design improvement
- practical construction decisions
- curb appeal
- interior livability
- overall value alignment

The work could not feel disconnected or overbuilt. It needed to feel cohesive.

## The approach

The planning process focused on how the house would be experienced as a whole, not just room by room.

That meant looking at:

- exterior presence
- landscape integration
- renovation sequencing
- material decisions
- how updates inside and outside would support one another

This is where integrated thinking matters. A renovation should not feel like separate decisions stacked on top of each other. It should feel like one clear direction.

## Scope of work

For this project, the work included:

- interior and exterior renovation strategy
- design development
- 3D modelling
- drawing package coordination
- construction execution thinking

This allowed decisions to be tested early before becoming expensive late changes.

## The outcome

The result is a cleaner, stronger, more resolved home.

What makes the project successful is not just the before-and-after difference. It is the fact that the final result feels coherent. The updates support one another. The house feels more intentional, more livable, and more aligned with its setting.

That is the difference between simply renovating a property and improving it with a clear plan.

## Why this kind of project matters

For homeowners and buyers, projects like this are useful examples of what happens when real estate thinking, design planning, and construction realities are aligned early.

That is where better outcomes come from.

If you are looking at an older Calgary home and wondering what is possible — or what is worth doing — I can help you evaluate the opportunity before you commit to the wrong path.`,
  },
];

export function getBlogPostBySlug(slug) {
  return blogPosts.find((p) => p.slug === slug) || null;
}

export function getFeaturedBlogPost() {
  return blogPosts.find((p) => p.featured) || blogPosts[0] || null;
}

export function formatBlogDate(isoDate) {
  try {
    return new Date(`${isoDate}T12:00:00`).toLocaleDateString("en-CA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return isoDate;
  }
}
